/**
 * Stock.
 *
 * The store room, not the menu - kilograms of chicken rather than burgers. The
 * two are joined by recipes (see RecipesPage), which is what lets a sale take
 * the right ingredients out.
 *
 * The stock card behind each item is the point: every figure traces back to the
 * movements that produced it, so "why do we have 3kg when we bought 10" has an
 * answer rather than an argument.
 */

import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    FiPlus, FiSearch, FiEdit2, FiAlertTriangle, FiBox, FiTrendingDown,
    FiClipboard, FiActivity
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { inventoryApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, StatCard, StatusBadge, Badge, EmptyState } from '../components/ui/Feedback';
import { Field, Input, NumberInput, Select, Textarea } from '../components/ui/Field';
import { selectLookups } from '../store/menuSlice';
import { money, quantity, formatDateTime } from '../utils/format';

const InventoryPage = () => {
    const lookups = useSelector(selectLookups);

    const [filters, setFilters] = useState({ search: '', categoryId: '', lowStockOnly: false });
    const [editing, setEditing] = useState(null);
    const [ledgerFor, setLedgerFor] = useState(null);
    const [adjusting, setAdjusting] = useState(null);

    const loadItems = useCallback(() => inventoryApi.listItems(filters), [filters]);
    const { data: items, loading, refetch } = useAsync(loadItems, [filters]);

    const loadCategories = useCallback(() => inventoryApi.listCategories(), []);
    const { data: categories } = useAsync(loadCategories, []);

    const { run: saveItem, busy: saving } = useAction(async (form) => {
        const payload = {
            ...form,
            stockCategoryId: form.stockCategoryId ? Number(form.stockCategoryId) : null,
            stockUomId: Number(form.stockUomId),
            purchaseUomId: form.purchaseUomId ? Number(form.purchaseUomId) : null,
            purchaseToStockRatio: Number(form.purchaseToStockRatio) || 1,
            openingQuantity: Number(form.openingQuantity) || 0,
            reorderLevel: Number(form.reorderLevel) || 0,
            averageCost: Number(form.averageCost) || 0
        };
        return form.Stock_Item_ID
            ? inventoryApi.updateItem(form.Stock_Item_ID, payload)
            : inventoryApi.createItem(payload);
    }, { successMessage: 'Stock item saved.', onSuccess: () => { setEditing(null); refetch(); } });

    /**
     * Open the editor on an existing item.
     *
     * The grid rows come from VW_STOCK_BALANCE, which carries the unit and
     * category as names rather than ids - nothing a Select can preselect. So
     * the full row is fetched first, and the form opens on real values instead
     * of blanks that a save would write back over the item.
     */
    const openEditor = async (row) => {
        try {
            const { item } = await inventoryApi.getItem(row.Stock_Item_ID);
            setEditing({
                Stock_Item_ID: item.Stock_Item_ID,
                stockItemCode: item.Stock_Item_Code || '',
                stockItemName: item.Stock_Item_Name,
                stockCategoryId: item.Stock_Category_ID ?? '',
                stockUomId: item.Stock_UOM_ID ?? '',
                purchaseUomId: item.Purchase_UOM_ID ?? '',
                purchaseToStockRatio: item.Purchase_To_Stock_Ratio ?? 1,
                openingQuantity: 0,
                reorderLevel: item.Reorder_Level ?? 0,
                averageCost: item.Average_Cost ?? 0
            });
        } catch (err) {
            toast.error(err.message);
        }
    };

    const totalValue = (items || []).reduce((sum, row) => sum + Number(row.Stock_Value || 0), 0);
    const lowCount = (items || []).filter((row) => row.Stock_State === 'LOW').length;
    const outCount = (items || []).filter((row) => row.Stock_State === 'OUT_OF_STOCK').length;

    const columns = [
        {
            key: 'Stock_Item_Name',
            header: 'Item',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{row.Stock_Item_Name}</p>
                    <p className="truncate text-xs text-slate-500">
                        {row.Category_Name || 'Uncategorised'}
                        {row.Stock_Item_Code && ` · ${row.Stock_Item_Code}`}
                    </p>
                </div>
            )
        },
        {
            key: 'Current_Quantity',
            header: 'In stock',
            align: 'right',
            render: (row) => (
                <div>
                    <p className="font-medium">
                        {quantity(row.Current_Quantity)}
                        <span className="ml-1 text-xs font-normal text-slate-500">{row.Stock_Unit}</span>
                    </p>
                    {row.Reorder_Level > 0 && (
                        <p className="text-xs text-slate-500">reorder at {quantity(row.Reorder_Level)}</p>
                    )}
                </div>
            )
        },
        {
            key: 'Stock_State',
            header: 'State',
            render: (row) => <StatusBadge status={row.Stock_State} />
        },
        {
            key: 'Average_Cost',
            header: 'Avg cost',
            align: 'right',
            render: (row) => (
                <span className="text-slate-600">{money(row.Average_Cost, 2)}</span>
            )
        },
        {
            key: 'Stock_Value',
            header: 'Value',
            align: 'right',
            render: (row) => <span className="font-medium">{money(row.Stock_Value)}</span>
        },
        {
            key: 'Drift',
            header: '',
            render: (row) => (
                // A non-zero drift means something wrote a quantity outside the
                // ledger. Rare, but worth surfacing rather than hiding.
                Math.abs(Number(row.Drift) || 0) > 0.001 ? (
                    <span
                        className="flex items-center gap-1 text-xs text-amber-600"
                        title={`The ledger says ${quantity(row.Ledger_Balance)}`}
                    >
                        <FiAlertTriangle className="h-3.5 w-3.5" /> drift
                    </span>
                ) : null
            )
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end gap-1">
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        onClick={(event) => { event.stopPropagation(); setLedgerFor(row); }}
                        title="Stock card"
                    >
                        <FiActivity className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        className="btn-ghost btn-sm !px-2"
                        title="Edit"
                        onClick={(event) => { event.stopPropagation(); openEditor(row); }}
                    >
                        <FiEdit2 className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page">
            <PageHeader
                title="Inventory"
                subtitle="What is in the store room"
                actions={(
                    <>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setAdjusting({ adjustmentType: 'WASTAGE' })}
                        >
                            <FiTrendingDown className="h-4 w-4" /> Record wastage
                        </button>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setAdjusting({ adjustmentType: 'COUNT' })}
                        >
                            <FiClipboard className="h-4 w-4" /> Stock count
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setEditing({
                                stockItemCode: '', stockItemName: '', stockCategoryId: '',
                                stockUomId: '', purchaseUomId: '', purchaseToStockRatio: 1,
                                openingQuantity: 0, reorderLevel: 0, averageCost: 0
                            })}
                        >
                            <FiPlus className="h-4 w-4" /> New item
                        </button>
                    </>
                )}
            />

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                <StatCard
                    label="Stock value"
                    value={`Rs. ${money(totalValue)}`}
                    sub={`${items?.length || 0} items`}
                    icon={FiBox}
                    tone="brand"
                />
                <StatCard
                    label="Low"
                    value={lowCount}
                    sub="At or below the reorder level"
                    icon={FiAlertTriangle}
                    tone={lowCount ? 'amber' : 'slate'}
                    onClick={() => setFilters({ ...filters, lowStockOnly: true })}
                />
                <StatCard
                    label="Out of stock"
                    value={outCount}
                    sub={outCount ? 'Sales still go through' : 'Nothing has run out'}
                    icon={FiTrendingDown}
                    tone={outCount ? 'red' : 'slate'}
                    onClick={() => setFilters({ ...filters, lowStockOnly: true })}
                />
            </div>

            <div className="toolbar">
                <div className="relative w-full sm:min-w-[16rem] sm:flex-1">
                    <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        className="input pl-9"
                        placeholder="Search stock items"
                        value={filters.search}
                        onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                    />
                </div>

                <select
                    className="input w-full sm:w-48"
                    value={filters.categoryId}
                    onChange={(event) => setFilters({ ...filters, categoryId: event.target.value })}
                >
                    <option value="">All categories</option>
                    {(categories || []).map((c) => (
                        <option key={c.Stock_Category_ID} value={c.Stock_Category_ID}>{c.Category_Name}</option>
                    ))}
                </select>

                <button
                    type="button"
                    className={filters.lowStockOnly ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setFilters({ ...filters, lowStockOnly: !filters.lowStockOnly })}
                >
                    Low stock only
                </button>
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={items || []}
                loading={loading}
                rowKey={(row) => row.Stock_Item_ID}
                onRowClick={setLedgerFor}
                emptyTitle="No stock items"
                emptyDescription="Add ingredients here, then link them to menu items with recipes."
                emptyIcon={FiBox}
            />

            {/* Editor */}
            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing?.Stock_Item_ID ? 'Edit stock item' : 'New stock item'}
                width="md"
                dismissible={!saving}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => saveItem(editing)}
                            disabled={saving || !editing?.stockItemName || !editing?.stockUomId}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </>
                )}
            >
                {editing && (
                    <div className="space-y-3">
                        <Field label="Name" required>
                            <Input
                                value={editing.stockItemName}
                                onChange={(e) => setEditing({ ...editing, stockItemName: e.target.value })}
                                placeholder="Chicken Boneless"
                                data-autofocus
                            />
                        </Field>

                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Code">
                                <Input
                                    value={editing.stockItemCode}
                                    onChange={(e) => setEditing({ ...editing, stockItemCode: e.target.value })}
                                />
                            </Field>
                            <Field label="Category">
                                <Select
                                    value={editing.stockCategoryId}
                                    onChange={(e) => setEditing({ ...editing, stockCategoryId: e.target.value })}
                                >
                                    <option value="">Choose…</option>
                                    {(categories || []).map((c) => (
                                        <option key={c.Stock_Category_ID} value={c.Stock_Category_ID}>
                                            {c.Category_Name}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                        </div>

                        <div className="rounded-lg bg-slate-50 p-3">
                            <p className="mb-2 text-xs text-slate-500">
                                Stock is held in the recipe unit and bought in the delivery unit.
                                Cooking oil arrives in 5-litre cans but a recipe asks for millilitres,
                                so the ratio there is 5000.
                            </p>

                            <div className="grid gap-3 sm:grid-cols-3">
                                <Field label="Recipe unit" required>
                                    <Select
                                        value={editing.stockUomId}
                                        onChange={(e) => setEditing({ ...editing, stockUomId: e.target.value })}
                                    >
                                        <option value="">Choose…</option>
                                        {lookups.uoms.map((u) => (
                                            <option key={u.UOM_ID} value={u.UOM_ID}>{u.UOM_Description}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Purchase unit">
                                    <Select
                                        value={editing.purchaseUomId}
                                        onChange={(e) => setEditing({ ...editing, purchaseUomId: e.target.value })}
                                    >
                                        <option value="">Same</option>
                                        {lookups.uoms.map((u) => (
                                            <option key={u.UOM_ID} value={u.UOM_ID}>{u.UOM_Description}</option>
                                        ))}
                                    </Select>
                                </Field>

                                <Field label="Ratio">
                                    <NumberInput
                                        step="0.000001"
                                        value={editing.purchaseToStockRatio}
                                        onChange={(e) => setEditing({ ...editing, purchaseToStockRatio: e.target.value })}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-3">
                            {!editing.Stock_Item_ID && (
                                <Field label="Opening quantity" hint="Posted as an opening movement.">
                                    <NumberInput
                                        value={editing.openingQuantity}
                                        onChange={(e) => setEditing({ ...editing, openingQuantity: e.target.value })}
                                    />
                                </Field>
                            )}
                            <Field label="Reorder level" hint="Below this triggers the low-stock alert.">
                                <NumberInput
                                    value={editing.reorderLevel}
                                    onChange={(e) => setEditing({ ...editing, reorderLevel: e.target.value })}
                                />
                            </Field>
                            {/* Re-averaged by every purchase, so it is an opening
                                figure rather than something to edit later. */}
                            {!editing.Stock_Item_ID ? (
                                <Field label="Cost per recipe unit">
                                    <NumberInput
                                        step="0.0001"
                                        value={editing.averageCost}
                                        onChange={(e) => setEditing({ ...editing, averageCost: e.target.value })}
                                    />
                                </Field>
                            ) : (
                                <Field label="Cost per recipe unit" hint="Set by purchases; not edited here.">
                                    <Input value={money(editing.averageCost, 2)} readOnly disabled />
                                </Field>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <StockLedgerModal item={ledgerFor} onClose={() => setLedgerFor(null)} />

            <AdjustmentModal
                preset={adjusting}
                items={items || []}
                reasons={lookups.wastageReasons}
                onClose={() => setAdjusting(null)}
                onSaved={refetch}
            />
        </div>
    );
};

/* ------------------------------- stock card ------------------------------- */

const StockLedgerModal = ({ item, onClose }) => {
    const load = useCallback(
        () => (item ? inventoryApi.getLedger(item.Stock_Item_ID, {}) : Promise.resolve([])),
        [item]
    );
    const { data: ledger, loading } = useAsync(load, [item], { immediate: !!item });

    return (
        <Modal
            open={!!item}
            onClose={onClose}
            title={item?.Stock_Item_Name || ''}
            subtitle={item
                ? `${quantity(item.Current_Quantity)} ${item.Stock_Unit} in stock · Rs. ${money(item.Stock_Value)}`
                : ''}
            width="lg"
        >
            {loading ? (
                <p className="py-8 text-center text-sm text-slate-500">Loading movements…</p>
            ) : !ledger?.length ? (
                <EmptyState title="No movements" icon={FiActivity} />
            ) : (
                <table className="table">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Movement</th>
                            <th>Reference</th>
                            <th className="text-right">Quantity</th>
                            <th className="text-right">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ledger.map((row) => (
                            <tr key={row.Stock_Transaction_ID}>
                                <td className="whitespace-nowrap text-slate-600">
                                    {formatDateTime(row.Transaction_Date_Time)}
                                </td>
                                <td>{row.Stock_Transaction_Type_Name}</td>
                                <td className="text-xs text-slate-500">{row.Reference_Number || '—'}</td>
                                <td className={`tabular text-right font-medium
                                                ${row.Direction > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                    {row.Direction > 0 ? '+' : '−'}{quantity(row.Quantity)}
                                </td>
                                <td className="tabular text-right">{quantity(row.Balance_After)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Modal>
    );
};

/* ------------------------- wastage / count dialog ------------------------- */

const AdjustmentModal = ({ preset, items, reasons, onClose, onSaved }) => {
    const [lines, setLines] = useState([]);
    const [reasonId, setReasonId] = useState('');
    const [remarks, setRemarks] = useState('');

    const isCount = preset?.adjustmentType === 'COUNT';

    const { run: post, busy } = useAction(
        () => inventoryApi.createAdjustment({
            adjustmentType: preset.adjustmentType,
            wastageReasonId: reasonId ? Number(reasonId) : null,
            remarks: remarks.trim() || null,
            items: lines
                .filter((line) => line.stockItemId)
                .map((line) => ({
                    stockItemId: Number(line.stockItemId),
                    quantity: isCount ? undefined : Number(line.quantity) || 0,
                    countedQuantity: isCount ? Number(line.countedQuantity) || 0 : undefined,
                    remarks: line.remarks || null
                }))
        }),
        {
            successMessage: isCount ? 'Stock count posted.' : 'Wastage recorded.',
            onSuccess: () => { setLines([]); setReasonId(''); setRemarks(''); onSaved(); onClose(); }
        }
    );

    return (
        <Modal
            open={!!preset}
            onClose={onClose}
            title={isCount ? 'Stock count' : 'Record wastage'}
            subtitle={isCount
                ? 'Enter what is physically on the shelf. The difference is posted for you.'
                : 'Spoilage, spills and anything that left without being sold.'}
            width="lg"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => post()}
                        disabled={busy || !lines.some((line) => line.stockItemId)}
                    >
                        {busy ? 'Posting…' : isCount ? 'Post count' : 'Record wastage'}
                    </button>
                </>
            )}
        >
            <div className="space-y-3">
                {!isCount && (
                    <Field label="Reason" required>
                        <Select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
                            <option value="">Choose…</option>
                            {reasons.map((r) => (
                                <option key={r.Wastage_Reason_ID} value={r.Wastage_Reason_ID}>
                                    {r.Wastage_Reason_Name}
                                </option>
                            ))}
                        </Select>
                    </Field>
                )}

                <div className="space-y-2">
                    {lines.map((line, index) => {
                        const stockItem = items.find(
                            (i) => String(i.Stock_Item_ID) === String(line.stockItemId)
                        );

                        return (
                            <div key={index} className="flex items-end gap-2">
                                <Field label={index === 0 ? 'Item' : null} className="flex-1">
                                    <Select
                                        value={line.stockItemId}
                                        onChange={(e) => setLines(lines.map((l, i) =>
                                            (i === index ? { ...l, stockItemId: e.target.value } : l)))}
                                    >
                                        <option value="">Choose…</option>
                                        {items.map((i) => (
                                            <option key={i.Stock_Item_ID} value={i.Stock_Item_ID}>
                                                {i.Stock_Item_Name}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>

                                {isCount && stockItem && (
                                    <div className="w-24 pb-2 text-right text-xs text-slate-500">
                                        system
                                        <br />
                                        <span className="tabular font-medium text-slate-700">
                                            {quantity(stockItem.Current_Quantity)}
                                        </span>
                                    </div>
                                )}

                                <Field
                                    label={index === 0 ? (isCount ? 'Counted' : 'Quantity') : null}
                                    className="w-32"
                                >
                                    <NumberInput
                                        value={isCount ? (line.countedQuantity ?? '') : (line.quantity ?? '')}
                                        onChange={(e) => setLines(lines.map((l, i) => (i === index
                                            ? { ...l, [isCount ? 'countedQuantity' : 'quantity']: e.target.value }
                                            : l)))}
                                    />
                                </Field>

                                <button
                                    type="button"
                                    className="btn-ghost btn-sm mb-1 !px-2 text-red-600"
                                    onClick={() => setLines(lines.filter((_, i) => i !== index))}
                                >
                                    Remove
                                </button>
                            </div>
                        );
                    })}

                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => setLines([...lines, { stockItemId: '', quantity: '', countedQuantity: '' }])}
                    >
                        <FiPlus className="h-3.5 w-3.5" /> Add a line
                    </button>
                </div>

                <Field label="Remarks">
                    <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                </Field>
            </div>
        </Modal>
    );
};

export default InventoryPage;
